import { useCallback, useEffect, useRef, useState } from "react";
import { createPaymentSheet } from "../api/stripeApi/paymentSheet";
import { markInflight, clearInflight } from "../utils/stripeCharge";

/**
 * Contract-charge gate for the Stripe branch, mirroring mobile's
 * `chargeViaStripe`. Exposes `chargeViaStripe(contractId)` returning a Promise
 * that resolves `true` only after the PaymentIntent reaches `succeeded`, and
 * `false` on cancel/failure — so a caller advances contract status ONLY on a
 * real, confirmed charge (R1/SEC-8). Also returns modal state to render
 * <PaymentElementModal> and a `preparing` flag for the pre-modal round-trip.
 *
 * SEC-2 (replay) — layered:
 *  1. Same-instance re-entry (double-click / a second call before the first
 *     settles) is blocked by a SYNCHRONOUS `busyRef` set at function entry —
 *     BEFORE the network mint — so two rapid clicks can't spawn two
 *     PaymentIntents even though React hasn't re-rendered the disabled button
 *     yet. The second call returns the in-flight promise.
 *  2. A per-contract sessionStorage marker persists across a reload/3DS return so
 *     the flow can tell a charge was already initiated for this contract.
 *  3. The authoritative dedup is server-side per-contract idempotency
 *     (`payment/payment-sheet` returns one PI per contract); sessionStorage is
 *     per-tab and cannot guard a second tab, so the server is the real guard.
 */
export const useStripeContractCharge = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const resolverRef = useRef(null);
  const contractIdRef = useRef(null);
  const promiseRef = useRef(null);
  const busyRef = useRef(false);

  const settle = useCallback((paid) => {
    clearInflight(contractIdRef.current);
    busyRef.current = false;
    setModalVisible(false);
    setClientSecret(null);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    promiseRef.current = null;
    contractIdRef.current = null;
    if (resolve) {
      resolve(Boolean(paid));
    }
  }, []);

  // Resolve a dangling charge `false` if the component unmounts mid-flow (e.g. a
  // plain nav-away), so an awaiting caller never hangs.
  useEffect(() => () => settle(false), [settle]);

  const chargeViaStripe = useCallback(async (contractId) => {
    // SEC-2 #1: synchronous re-entry guard set BEFORE the await, so a rapid
    // double-click can't mint a second PaymentIntent while the first request is
    // still in flight. Return the in-flight promise if one exists.
    if (busyRef.current) {
      return promiseRef.current ?? false;
    }
    busyRef.current = true;
    setPreparing(true);
    const data = await createPaymentSheet(contractId);
    setPreparing(false);
    // The charge already settled for this contract, so there is nothing to pay:
    // resolve TRUE and let the caller advance the contract. Without this the
    // payer is permanently stuck — their money is captured, but accept is never
    // reached because the charge step aborts first (GIG-130 Q3).
    if (data?.alreadyPaid) {
      clearInflight(contractId);
      busyRef.current = false;
      return true;
    }
    if (!data?.paymentIntent) {
      busyRef.current = false;
      return false;
    }
    contractIdRef.current = contractId;
    markInflight(contractId);
    setClientSecret(data.paymentIntent);
    setModalVisible(true);
    const promise = new Promise((resolve) => {
      resolverRef.current = resolve;
    });
    promiseRef.current = promise;
    return promise;
  }, []);

  const onSuccess = useCallback(() => settle(true), [settle]);
  const onCancel = useCallback(() => settle(false), [settle]);

  return {
    chargeViaStripe,
    preparing,
    modalProps: {
      visible: modalVisible,
      mode: "payment",
      clientSecret,
      onSuccess,
      onCancel,
    },
  };
};
